import { useEffect, useRef, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';

export const termsTypes = ['privacyPolicy', 'termsOfService', 'cookiePolicy', 'usePolicy'];

export type TermsTypes = (typeof termsTypes)[number];

declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

const EmbedTerms = () => {
  let [searchParams] = useSearchParams();
  const termType = searchParams.get('termType') as TermsTypes;
  const navigate = useNavigate();
  const embedRef = useRef<HTMLDivElement | null>(null);
  const termsScriptId = 'getterms-embed-js';
  const { i18n, t } = useTranslation();

  const termsDocumentType = {
    privacyPolicy: 'privacy',
    termsOfService: 'tos',
    cookiePolicy: 'cookie',
    usePolicy: 'aup',
  }[termType as TermsTypes];

  useEffect(() => {
    const termsScript = document.createElement('script');
    termsScript.src = 'https://app.getterms.io/dist/js/embed.js';
    termsScript.id = termsScriptId;
    document.body.appendChild(termsScript);

    const googleTranslateScript = document.createElement('script');
    googleTranslateScript.id = 'googleTranslate';
    googleTranslateScript.src =
      'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    googleTranslateScript.async = true;
    document.body.appendChild(googleTranslateScript);

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement({ pageLanguage: i18n.language }, 'terms');
    };

    return () => {
      document.body.removeChild(googleTranslateScript);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (termType && !termsTypes.includes(termType)) {
      document.getElementById(termsScriptId)?.remove();
      navigate('/');
    }
  }, [navigate, termType]);

  const helmetText = t(`footer.links.${termsTypes.indexOf(termType) + 4}`);

  return (
    <>
      <Helmet>
        <title>{t(helmetText)}</title>
        <meta name="description" content={String(t(helmetText))} />
      </Helmet>
      <div className="box-border min-h-screen w-screen overflow-y-auto bg-gradient1 p-[16px] pb-[24px] font-IBMPlexSansRegular">
        <div id="terms" className="md:p-[16px]" />
        <div
          className="getterms-document-embed mx-auto md:max-w-[1024px] md:pt-[24px] [&>h2]:my-[16px] [&>h3]:my-[16px] [&>h4]:my-[16px]"
          data-getterms="p86go"
          data-getterms-document={termsDocumentType}
          data-getterms-lang="en-us"
          data-getterms-mode="direct"
          ref={embedRef}
        />
      </div>
    </>
  );
};

export default EmbedTerms;
